import { useRolodexEdit } from 'features/fde'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import { useResolvedChart } from 'features/timeSeries'
import {
  serializeDateConfig,
  serializeSourceConfigs,
} from 'features/timeSeries/utils/chartSerialization'
import { MutableRefObject, useCallback, useMemo } from 'react'
import { DateConfig, RolodexEntryType, SourceConfig, YAxisMode } from 'types'

type UseChartSaveArgs = {
  chartID: string
  selectedSourceConfigs: SourceConfig[]
  yAxisMode: YAxisMode
  dateConfig: DateConfig
  tags?: Record<string, string | null | undefined>
  derivedValueIdsToDeleteRef?: MutableRefObject<Set<string>>
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
}

const compareSourceConfigs = (
  a: SourceConfig[],
  b: SourceConfig[]
): boolean => {
  if (a.length !== b.length) return false

  // Sort both arrays by sourceId for consistent comparison
  const sortedA = [...a].sort((x, y) => x.sourceId.localeCompare(y.sourceId))
  const sortedB = [...b].sort((x, y) => x.sourceId.localeCompare(y.sourceId))

  return sortedA.every((configA, index) => {
    const configB = sortedB[index]
    return (
      configA.sourceId === configB.sourceId &&
      configA.type === configB.type &&
      configA.color === configB.color &&
      configA.hidden === configB.hidden &&
      JSON.stringify(configA.yAxisRange) === JSON.stringify(configB.yAxisRange)
    )
  })
}

const compareDateConfigs = (a: DateConfig, b: DateConfig): boolean => {
  if (a.mode !== b.mode) return false

  if (a.mode === 'absolute' && b.mode === 'absolute') {
    return (
      a.startDate.getTime() === b.startDate.getTime() &&
      a.endDate.getTime() === b.endDate.getTime()
    )
  }

  if (a.mode === 'relative' && b.mode === 'relative') {
    const startEqual =
      a.startDateLookback.value === b.startDateLookback.value &&
      a.startDateLookback.unit === b.startDateLookback.unit

    const endEqual =
      JSON.stringify(a.endDateLookback) === JSON.stringify(b.endDateLookback)

    const cadenceEqual = a.updateCadenceSeconds === b.updateCadenceSeconds

    return startEqual && endEqual && cadenceEqual
  }

  return false
}

export const useChartSave = ({
  chartID,
  selectedSourceConfigs,
  yAxisMode,
  dateConfig,
  tags,
  derivedValueIdsToDeleteRef,
  onChartSaved,
}: UseChartSaveArgs) => {
  const { rolodexPatch, rolodexDelete } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()

  const validChartID = chartID && chartID.trim() !== '' ? chartID : undefined
  const { chart: savedChart, mutate: mutateChart } =
    useResolvedChart(validChartID)

  const { entries, forceRefresh: refreshEntry } = useRolodexEntries(
    validChartID
      ? { ids: [validChartID], type: [RolodexEntryType.CHART] }
      : undefined
  )

  const lastSavedAt = entries?.[0]?.updated_at
    ? new Date(entries[0].updated_at)
    : null

  const nextDataCamel = useMemo(() => {
    return {
      sourceConfigs: selectedSourceConfigs,
      yAxisMode,
      dateConfig,
    }
  }, [selectedSourceConfigs, yAxisMode, dateConfig])

  const hasUnsavedChanges = useMemo(() => {
    if (!savedChart) {
      return nextDataCamel.sourceConfigs.length > 0
    }

    if (
      derivedValueIdsToDeleteRef &&
      derivedValueIdsToDeleteRef.current.size > 0
    ) {
      return true
    }

    const sourceConfigsMatch = compareSourceConfigs(
      nextDataCamel.sourceConfigs,
      savedChart.sourceConfigs
    )

    const yAxisModeMatch = nextDataCamel.yAxisMode === savedChart.yAxisMode

    const dateConfigMatch = compareDateConfigs(
      nextDataCamel.dateConfig,
      savedChart.dateConfig
    )

    return !sourceConfigsMatch || !yAxisModeMatch || !dateConfigMatch
  }, [nextDataCamel, savedChart, derivedValueIdsToDeleteRef])

  const saveChart = useCallback(async () => {
    if (!chartID || chartID.trim() === '') {
      const error = 'Cannot save chart: Invalid chart ID'
      errorNotification(error)
      return
    }

    const patchEntry = {
      id: chartID,
      type: RolodexEntryType.CHART,
      data: {
        source_configs: serializeSourceConfigs(nextDataCamel.sourceConfigs),
        y_axis_mode: nextDataCamel.yAxisMode,
        date_config: serializeDateConfig(nextDataCamel.dateConfig),
      },
      tags: tags ?? {},
    }

    try {
      const isNewChart = !savedChart

      // Save the chart
      await rolodexPatch([patchEntry])

      // Delete derived values that were removed by the user
      if (
        derivedValueIdsToDeleteRef &&
        derivedValueIdsToDeleteRef.current.size > 0
      ) {
        await Promise.all(
          Array.from(derivedValueIdsToDeleteRef.current).map((id) =>
            rolodexDelete(id, false)
          )
        )
        derivedValueIdsToDeleteRef.current.clear()
      }

      if (onChartSaved) {
        await onChartSaved(chartID, isNewChart)
      }

      await mutateChart()
      await refreshEntry()

      successNotification('Chart saved successfully!')
    } catch (err) {
      console.error('Chart save failed', err)
      errorNotification(`Failed to save chart: ${err}`)
      throw err
    }
  }, [
    chartID,
    nextDataCamel,
    rolodexPatch,
    rolodexDelete,
    tags,
    derivedValueIdsToDeleteRef,
    mutateChart,
    refreshEntry,
    successNotification,
    errorNotification,
    savedChart,
    onChartSaved,
  ])

  return {
    saveChart,
    hasUnsavedChanges,
    lastSavedAt,
  }
}
